import React from "react";

import { ContentModule } from "utils/orm";
import { HtmlBlock, HtmlModule } from "components/HtmlModule";
import { RichTextModule, RichTextBlock } from "components/RichTextModule";
import { BlockQuoteModule, BlockQuoteBlock } from "components/BlockQuoteModule";
import { DividerModule, DividerBlock } from "components/DividerModule";
import { EmbedModule, EmbedBlock } from "components/EmbedModule";
import { JWVideoModule, JWVideoBlock } from "components/JWVideoModule";
import { TocModule, TocModuleProps, TocEntities } from "components/TocModule";
import { CompanyModule, CompanyProfileBlock } from "components/CompanyModule";
import { ResponsiveImage } from "components/ResponsiveImage";
import { SponsorInlineBlock, SponsorInlineModule } from "./SponsorInlineModule";

import responsiveImageStyles from "./responsiveImageModule.module.scss";
import { JobQuizBlock, JobQuizModule } from "./JobQuizModule";

// Image blocks are transformed into
// responsive image components for display
export interface ImageBlock {
    alignment: string | null;
    aspectRatio: string;
    caption: string | null;
    heading: string | null;
    inToc: boolean;
    linkUrl: string | null;
    type: string;
    image: {
        altText: string | null;
        caption: string | null;
        credit: string | null;
        creditLink: string | null;
        height: number;
        width: number;
        url: string;
    }
}

interface HeadingModuleProps {
    heading: string;
    position: number;
    prefix: string;
    size: boolean;
    id: string;
    headerClassName?: string;
}

export interface ContentMapperProps {
    modules: ContentModule[];
    templateName: string;
    hasBullets?: boolean;
    prefix?: string;
    tocEntities?: TocEntities[];
}
interface JumpLinkProps {
    id: number;
}

const JumpLink: React.FC<JumpLinkProps> = (props) => {
    const { id } = props;
    return <span id={`${id}`} className="jump-link" />;
};

/**
 * Returns a heading either h2 or h3.
 * * If position is passed this is a bullet heading and will also contain numbering.
 * * If a prefix is passed we render the prefix before the number.
 * * If a prefix is passed the suffix of the number becomes a colon.
 * * If header is not passed we render nothing.
 * * If size is passed we render an H3.
 */
const HeadingModule: React.FC<HeadingModuleProps> = (props) => {
    const {
        prefix, heading, position, size, headerClassName
    } = props;
    const intro = position ? `${prefix || ""} ${position}${prefix ? ": " : ". "}`.trim() : "";
    const content = (
        <>
            {intro.length ? `${intro} ` : ""}
            <RichTextModule className="inline" type="richTextBlock" richText={heading} />
        </>
    );
    return size ? <h2 className={headerClassName}>{content}</h2> : <h3 className={headerClassName}>{content}</h3>;
};

const getResponsiveImage: React.FC<ImageBlock> = (props) => {
    const { alignment = "left", image } = props;
    const {
        height, width, url, altText, credit, creditLink
    } = image;

    // Because caption can be overwritten in the template.
    const caption = props.caption || image.caption || "";
    const aspect = height / width;
    const sizes = {
        xs: 313,
        sm: 630,
        md: 630,
        lg: 515,
        xl: 780
    };

    const mobileWidth = Math.ceil(width >= sizes.xs ? sizes.xs : width);
    const tabletWidth = Math.ceil(width >= sizes.sm ? sizes.sm : width);
    const laptopWidth = Math.ceil(width >= sizes.md ? sizes.md : width);
    const desktopWidth = Math.ceil(width >= sizes.lg ? sizes.lg : width);
    const bigScreenWidth = Math.ceil(width >= sizes.xl ? sizes.xl : width);

    const mobileHeight = Math.ceil(mobileWidth * aspect);
    const tabletHeight = Math.ceil(tabletWidth * aspect);
    const laptopHeight = Math.ceil(laptopWidth * aspect);
    const desktopHeight = Math.ceil(desktopWidth * aspect);
    const bigScreenHeight = Math.ceil(bigScreenWidth * aspect);

    return (
        <figure className={`${responsiveImageStyles.articleBodyImageModule} ${responsiveImageStyles[`pull${alignment}`]}`}>
            <span className={responsiveImageStyles.imageWrapper}>
                <ResponsiveImage
                    {...{
                        altText,
                        bigScreenHeight,
                        bigScreenWidth,
                        caption,
                        credit,
                        creditLink,
                        desktopHeight,
                        desktopWidth,
                        laptopHeight,
                        laptopWidth,
                        loading: "lazy",
                        mobileHeight,
                        mobileWidth,
                        tabletHeight,
                        tabletWidth,
                        url,
                    }}
                />
            </span>
        </figure>
    );
};

// The following linter issue is a bug in eslint
// eslint-disable-next-line no-undef
export const ContentMapper = (props: ContentMapperProps): JSX.Element[] | null => {
    const {
        modules = [], hasBullets, prefix, tocEntities
    } = props;
    const hasDividers: boolean = modules && modules.some((module: ContentModule) => module && module.type === "divider");
    let position: number = 0;
    let companyOrder = 0;
    const annotatedModules = modules.map((module) => {
        if (module.type === "companyProfileBlock") {
            companyOrder += 1;
            return {
                ...module,
                orderNum: companyOrder
            };
        }
        return module;
    });

    return annotatedModules.map((module: any) => {
        const {
            heading, type, id, inToc
        } = module;
        const size = !hasDividers || type === "divider";
        const hasBullet = !!heading && type !== "divider";
        const headerClassName = props.templateName === "listicle" ? "listicle-header" : null;
        position = hasBullets && hasBullet ? position + 1 : position;
        const HeadingProps = {
            prefix: hasBullets && hasBullet ? prefix : "",
            position: hasBullet ? position : 0,
            size,
            heading,
            id,
            headerClassName
        };

        const content: {
            // The following linter issue is a bug in eslint
            // eslint-disable-next-line no-undef
            [key: string]: (moduleProps: ContentModule) => JSX.Element;
        } = {
            richTextBlock: (moduleProps: RichTextBlock) => <RichTextModule {...moduleProps} />,
            blockquoteBlock: (moduleProps: BlockQuoteBlock) => <BlockQuoteModule {...moduleProps} />,
            htmlBlock: (moduleProps: HtmlBlock) => <HtmlModule {...moduleProps} />,
            embedBlock: (moduleProps: EmbedBlock) => <EmbedModule template="article" {...moduleProps} />,
            jwVideoBlock: (moduleProps: JWVideoBlock) => <JWVideoModule {...moduleProps} />,
            companyProfileBlock: (moduleProps: CompanyProfileBlock) => (
                <CompanyModule {...moduleProps} isArticle={!hasBullets} orderNum={module.orderNum} />
            ),
            divider: (moduleProps: DividerBlock) => <DividerModule {...moduleProps} />,
            imageBlock: getResponsiveImage,
            tocGenerator: (moduleProps: TocModuleProps) => (
                <TocModule entities={tocEntities} {...moduleProps} />
            ),
            sponsorInlineBlock: (moduleProps: SponsorInlineBlock) => <SponsorInlineModule {...moduleProps} />,
            jobQuizBlock: (moduleProps: JobQuizBlock) => <JobQuizModule {...moduleProps} />
        };

        return (
            content[module.type] && (
                <React.Fragment key={id}>
                    {/*
                        This span is a target for the TOC.
                        It is only injected when this content is included in the TOC.
                        We use css to hide it visually.
                        We use css to push it up the page an equal distance to that of the fixed navigation.
                        This allows our target to be in the viewport and not hidden by the fixed navigation.
                    */}
                    {inToc && <JumpLink {...{ id }} />}
                    {heading && module.type !== "divider" && <HeadingModule {...HeadingProps} />}
                    {" "}
                    {content[module.type](module)}
                </React.Fragment>
            )
        );
    });
};
